import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { Close } from 'svgIcons'
import {
  ChannelFeedTagBox_TagCollectionFragment,
  TagFragment,
  useGqlTagBottomSheetQuery,
} from 'generated/graphql'
import BottomSheet, {
  IFBottomSheetProps,
} from '@app.components/BottomSheet/BottomSheet'
import Button from '@app.components/Button'
import ResetTag from 'svgIcons/ResetTag'

gql`
  query gqlTagBottomSheet {
    TagBottomSheet: recommendNewsItemTagCollection {
      ...ChannelFeedTagBox_TagCollection
    }
  }
`

type IFProps = IFBottomSheetProps

const TagBottomSheet: React.FC<IFProps> = ({ onClose, ...props }) => {
  const router = useRouter()

  const { data } = useGqlTagBottomSheetQuery()
  const [selectedMenu, setSelectedMenu] = useState<string | null>(null)

  const filteredData = useMemo(() => {
    const filteredData: ChannelFeedTagBox_TagCollectionFragment[] = []
    if (!data || !data.TagBottomSheet) return
    data.TagBottomSheet.forEach((tagCollection) => {
      if (tagCollection.tags.nodes.length !== 0)
        filteredData.push(tagCollection)
    })
    return filteredData
  }, [data])

  useEffect(() => {
    if (!filteredData || filteredData.length === 0) return
    setSelectedMenu(filteredData[0].title)
    return () => {}
  }, [filteredData])

  const handleClickMenu = (menu: string) => {
    setSelectedMenu(menu)
  }

  const addTag = (tag: string) => {
    const tags = router.query.tags ? `${router.query.tags}`.split(',') : []
    const tagsSet = new Set(tags)
    tagsSet.add(tag)
    const newTags = Array.from(tagsSet).join(',')
    router.push(
      {
        query: {
          ...router.query,
          tags: newTags,
        },
      },
      undefined,
      {
        scroll: false,
      },
    )
  }

  const deleteTag = (tag: string) => {
    const newQuery = router.query
    const tags = router.query.tags ? `${router.query.tags}`.split(',') : []
    const tagsSet = new Set(tags)
    tagsSet.delete(tag)
    const newTags = Array.from(tagsSet).join(',')
    newQuery.tags = newTags
    router.push(
      {
        query: newQuery,
      },
      undefined,
      {
        scroll: false,
      },
    )
  }

  const handleReset = () => {
    const newQuery = { ...router.query }
    delete newQuery.tags
  }

  const dataSet = useMemo(() => {
    const dataSet: {
      [key: string]: TagFragment[]
    } = {}
    if (!filteredData) return null
    filteredData.forEach((tagItem) => {
      dataSet[tagItem.title] = tagItem.tags.nodes.map((tag) => tag)
    })
    return dataSet
  }, [filteredData])

  const selectedTagIds = useMemo(() => {
    return router.query.tags ? `${router.query.tags}`.split(',') : []
  }, [router.query.tags])

  const selectedTags = useMemo(() => {
    if (!data || !data.TagBottomSheet) return []

    const tags = data.TagBottomSheet.reduce<TagFragment[]>((prev, curr) => {
      return prev.concat(curr.tags.nodes)
    }, [])

    const uniqueTagMap = tags.reduce((acc, tag) => {
      acc.set(tag.id, tag)
      return acc
    }, new Map())

    const uniqueTags = Array.from(uniqueTagMap.values())

    return uniqueTags.filter((tag) => selectedTagIds.includes(tag.id))
  }, [router.query.tags, data])

  if (!dataSet || !selectedMenu) return null
  return (
    <BottomSheet onClose={onClose} {...props}>
      <StyledWrapper>
        <div className="top">
          <div className="tag-header">
            {Object.keys(dataSet).map((header, idx) => (
              <button
                key={idx}
                className="tag-header-item"
                onClick={() => handleClickMenu(header)}
                data-active={header === selectedMenu}
              >
                {header}
              </button>
            ))}
          </div>
          <div className="tag-list">
            {dataSet[selectedMenu].map((tag, idx) => (
              <button
                className="tag-item"
                key={idx}
                onClick={() =>
                  selectedTagIds.find((tagId) => tagId === tag.id)
                    ? deleteTag(tag.id)
                    : addTag(tag.id)
                }
                data-selected={
                  !!selectedTagIds.find((tagId) => tagId === tag.id)
                }
              >
                #{tag.name}
              </button>
            ))}
          </div>
        </div>
        <div className="bottom">
          <div className="selected-tag-list">
            {selectedTags.map((tag, idx) => (
              <button
                className="tag-item"
                key={idx}
                onClick={() => deleteTag(tag.id)}
              >
                <div>{tag.name}</div>
                <div>
                  <Close />
                </div>
              </button>
            ))}
          </div>
          <div className="btn-list">
            <Link
              onClick={handleReset}
              href={{
                query: {
                  ...router.query,
                  tags: '',
                },
              }}
              scroll={false}
            >
              <a className="reset-btn">
                <ResetTag />
                <div>초기화</div>
              </a>
            </Link>
            <Button className="search-btn" onClick={onClose}>
              소식보기
            </Button>
          </div>
        </div>
      </StyledWrapper>
    </BottomSheet>
  )
}

export default TagBottomSheet

const StyledWrapper = styled.div`
  height: 400px;
  padding: 28px 20px 11px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .top {
    overflow-y: scroll;
    .tag-header {
      display: flex;
      gap: 15px;
      margin-bottom: 30px;
      .tag-header-item {
        font-size: 14px;
        font-weight: bold;
        background: none;
        border: none;
        padding: 0px;
        color: #bbb;
        &[data-active='true'] {
          color: #333;
        }
      }
    }
    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      overflow-y: scroll;
      flex-grow: 0;
      .tag-item {
        border: none;
        font-weight: 400;
        line-height: 1.43;
        border-radius: 30px;
        background-color: #eee;
        font-size: 12px;
        padding: 6px 12px;
        &[data-selected='true'] {
          background-color: #01a0ff;
          color: white;
        }
      }
    }
  }
  .bottom {
    .selected-tag-list {
      display: flex;
      overflow-x: scroll;
      gap: 5px;
      margin-bottom: 16px;
      .tag-item {
        color: hsl(0deg, 0%, 21%);
        font-size: 12px;
        background-color: #f2faff;
        padding: 10px 15px;
        border-radius: 5px;
        border: 1px solid #d9e9f3;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 10px;
        svg {
          width: 8px;
          height: 8px;
        }
      }
    }
    .btn-list {
      display: flex;
      .reset-btn {
        font-size: 12px;
        font-weight: bold;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 50px;
        flex-grow: 1;
      }
      .search-btn {
        font-size: 16px;
        height: 50px;
        flex-grow: 2;
      }
    }
  }
`
